var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import "../types.js";
import { registerSystem, DEPTHS } from "../ComponentRegistry.js";
import { PhysicsSystem } from "./Physics.js";
import { BV2 } from "../../utils/vector.js";
import { C as ComponentCategory, d as CollisionCategoryEnum } from "../../enums-86f58524.js";
import RAPIER from "@dimforge/rapier2d-compat";
import { L as LocomotionSchema } from "../../Locomotion-379972de.js";
import { R as RadiusSchema } from "../../Radius-89a665f8.js";
import { T as TransformSchema } from "../../Transform-9450b7b5.js";
import { C as CollisionsSchema } from "../../Collisions-9e9492fb.js";
import { R as RigidCircleSchema, a as RigidCircleResolverSchema } from "../../RigidCircle-ee2bcb73.js";
import "../../decorators/type.js";
import "ajv";
import "../../utils/clone.js";
import "bitecs";
import "../../utils/typehelpers.js";
import "../../global-33d5eebf.js";
import "../../utils/byteArray.js";
import "../entity/Types.js";
import "../../console/flags.js";
import "../../persist/persist.js";
import "../../console/editor.js";
import "../../console/floatingwindow.js";
import "lodash";
import "../../Description-c36d0b9a.js";
import "../../Attach-abbbd93e.js";
import "../../utils/windowSessionStorage.js";
import "pixi.js";
import "../../FrameRate-7ce72a03.js";
import "../../Physics-8d447f7a.js";
class RigidCircleSystem {
  constructor() {
    __publicField(this, "type", "RigidCircle");
    __publicField(this, "category", ComponentCategory.PHYSICS);
    __publicField(this, "schema", RigidCircleSchema);
    __publicField(this, "depth", DEPTHS.COLLISION - 1e-4);
    __publicField(this, "bodies", {});
  }
  init(entity, gameModel) {
    const transformSchema = gameModel.getTypedUnsafe(entity, TransformSchema);
    const position = transformSchema.position;
    const rigidCircle = gameModel.getTypedUnsafe(entity, RigidCircleSchema);
    const physicsSystem = gameModel.getSystem(PhysicsSystem);
    const engine = physicsSystem.getEngine(gameModel);
    const prevBody = this.bodies[entity];
    if (prevBody) {
      engine.removeRigidBody(prevBody);
    }
    const rigidBodyDesc = rigidCircle.isStatic ? RAPIER.RigidBodyDesc.fixed() : RAPIER.RigidBodyDesc.dynamic();
    rigidBodyDesc.setTranslation(position.x, position.y);
    const rigidBody = engine.createRigidBody(rigidBodyDesc);
    let colliderDesc = RAPIER.ColliderDesc.ball(rigidCircle.radius || RadiusSchema.store.radius[entity]).setMass(
      rigidCircle.mass
    );
    let filterMask = CollisionCategoryEnum.ALL;
    if (rigidCircle.collisionMask) {
      filterMask = rigidCircle.collisionMask.reduce((acc, val) => acc | val, 0);
    }
    const memberMask = rigidCircle.collisionCategory << 16;
    colliderDesc.setCollisionGroups(memberMask | filterMask);
    if (rigidCircle.isSensor) {
      colliderDesc = colliderDesc.setSensor(true);
    }
    if (rigidCircle.collisionEvents) {
      colliderDesc.setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    }
    const collider = engine.createCollider(colliderDesc, rigidBody);
    this.bodies[entity] = rigidBody;
    physicsSystem.colliderHandleMap.handleToEntity[collider.handle] = entity;
    physicsSystem.colliderHandleMap.entityToHandle[entity] = collider.handle;
    if (!gameModel.hasComponent(entity, "RigidCircleResolver")) {
      gameModel.addComponent(entity, "RigidCircleResolver");
    }
  }
  runAll(gameModel) {
    const entities = gameModel.getComponentActives("RigidCircle");
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const rigidCircle = gameModel.getTypedUnsafe(entity, RigidCircleSchema);
      let circle = this.bodies[entity];
      if (rigidCircle.disabled) {
        if (circle) {
          this.cleanup(entity, gameModel);
        }
        continue;
      } else {
        if (!circle) {
          this.init(entity, gameModel);
          circle = this.bodies[entity];
        }
      }
      const transformSchema = gameModel.getTypedUnsafe(entity, TransformSchema);
      const position = transformSchema.position;
      const locomotionSchema = gameModel.getTypedUnsafe(entity, LocomotionSchema);
      const velocity = locomotionSchema.velocity;
      const body = this.bodies[entity];
      body.setTranslation(position, true);
      let decayingVelocity = null;
      if (locomotionSchema.decayingVelocityTime > 0) {
        const decayTime = Math.max(150, locomotionSchema.decayingVelocityTime);
        const decayFactor = 0.25;
        if (locomotionSchema.decayingVelocityTime < decayTime) {
          const expDecay = Math.pow(1 - decayFactor, decayTime - locomotionSchema.decayingVelocityTime);
          decayingVelocity = BV2.lerpVector2d(
            locomotionSchema.decayingVelocityX,
            locomotionSchema.decayingVelocityY,
            0,
            0,
            expDecay
          );
          decayingVelocity[0] *= locomotionSchema.decayingVelocityScale;
          decayingVelocity[1] *= locomotionSchema.decayingVelocityScale;
        } else {
          locomotionSchema.decayingVelocityTime = decayTime;
          decayingVelocity = [locomotionSchema.decayingVelocityX, locomotionSchema.decayingVelocityY];
        }
        locomotionSchema.decayingVelocityTime -= gameModel.dt(entity);
      } else {
        locomotionSchema.decayingVelocityX = 0;
        locomotionSchema.decayingVelocityY = 0;
        locomotionSchema.decayingVelocityTime = 0;
      }
      body.setLinvel(
        {
          x: (velocity.x + (decayingVelocity ? decayingVelocity[0] : 0)) * 60,
          y: (velocity.y + (decayingVelocity ? decayingVelocity[1] : 0)) * 60
        },
        true
      );
    }
  }
  cleanup(entity, gameModel) {
    gameModel.removeComponent(entity, "RigidCircleResolver");
    const physicsSystem = gameModel.getSystem(PhysicsSystem);
    const engine = physicsSystem.getEngine(gameModel);
    const collisions = gameModel.getTypedUnsafe(gameModel.coreEntity, CollisionsSchema).collisionMap;
    if (collisions == null ? void 0 : collisions[entity]) {
      Object.keys(collisions[entity]).forEach((other) => {
        const otherKey = parseInt(other);
        if (collisions[otherKey]) {
          delete collisions[otherKey][entity];
        }
      });
      delete collisions[entity];
    }
    const handle = physicsSystem.colliderHandleMap.entityToHandle[entity];
    if (handle !== void 0) {
      const collider = engine.getCollider(handle);
      if (collider)
        engine.removeCollider(collider, false);
    }
    delete physicsSystem.colliderHandleMap.handleToEntity[handle];
    delete physicsSystem.colliderHandleMap.entityToHandle[entity];
    const body = this.bodies[entity];
    if (body) {
      try {
        engine.removeRigidBody(body);
      } catch (e) {
      }
      delete this.bodies[entity];
    }
  }
}
registerSystem(RigidCircleSystem);
class RigidCircleResolverSystem {
  constructor() {
    __publicField(this, "type", "RigidCircleResolver");
    __publicField(this, "category", ComponentCategory.PHYSICS);
    __publicField(this, "depth", DEPTHS.COLLISION + 1e-4);
    __publicField(this, "schema", RigidCircleResolverSchema);
  }
  runAll(gameModel) {
    const rigidCircleSystem = gameModel.getSystem(RigidCircleSystem);
    const entities = gameModel.getComponentActives("RigidCircleResolver");
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const circle = rigidCircleSystem.bodies[entity];
      const rigidCircle = gameModel.getTypedUnsafe(entity, RigidCircleSchema);
      const position = circle.translation();
      const positionX = position.x;
      const positionY = position.y;
      const transformSchema = gameModel.getTypedUnsafe(entity, TransformSchema);
      transformSchema.x = positionX;
      transformSchema.y = positionY;
      const locomotionSchema = gameModel.getTypedUnsafe(entity, LocomotionSchema);
      if (rigidCircle.velocityLock) {
        const velocity = circle.linvel();
        locomotionSchema.velocityX = velocity.x / 60;
        locomotionSchema.velocityY = velocity.y / 60;
      }
      if (rigidCircle.directionLock) {
        const direction = circle.rotation();
        const locomotionSchema2 = gameModel.getTypedUnsafe(entity, LocomotionSchema);
        locomotionSchema2.directionX = Math.cos(direction);
        locomotionSchema2.directionY = Math.sin(direction);
      }
    }
  }
}
registerSystem(RigidCircleResolverSystem);
export {
  RigidCircleSystem
};
//# sourceMappingURL=RigidCircle.js.map
